import React, { useEffect, useState, useContext } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Link, Typography, useBreakpoint, } from '@one-thd/sui-atomic-components';
import { B2BCreditCardsFilterType } from '../../../api/b2b/payments';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants/payments-table';
import { DELETED_CARD_MSG } from '../../../constants/response-success-messages';
import { getNewPageNumber, hasNoPaymentCards } from '../../../utils/payments';
import { Spinner } from '../../shared/spinner/spinner';
import { usePayments } from '../../../hooks/use-payments';
import { CreditCard } from '../credit-card/credit-card';
import { CreditCardSortSearchBar } from '../credit-card-sort-search-bar/credit-card-sort-search-bar';
import { useIsMounted } from '../../../hooks/use-is-mounted';
import { Alert } from '../../shared/alert/alert';
import { TAlertStatus } from '../../../models/common/alert-messages';
import { PaymentsHeader } from '../payments-header/payments-header';
import { CardType } from '../../../constants/payments';
import { CreditCardTypesTextMapping } from '../../../constants/credit-card';
import { CREDIT_CARDS_PAGE_URL } from '../../../constants/links';
import { Pagination } from '../../shared/pagination/pagination';
import { PAYMENTS_PAGE_SIZE } from '../../../constants/general';
import { scrollToTop } from '../../../utils/general';
import { cardSortByMap } from '../../../constants/sort';
import { PaymentsHeaderSecondaryButtons } from '../payments-header/payments-header-secondary-buttons';
import { NoPaymentCardsMessage } from '../../shared/no-payment-cards-message/no-payment-cards-message';
import { CardViewToggle } from '../card-view-toggle/card-view-toggle';
import { TableView } from './table-view';
import { setOtherButtonSubmissionAnalytics } from '../../../utils/payments-analytics';
import { useLoyaltyMembershipDetails } from '../../../hooks/use-loyalty-membership-details';
import { CustomerRole, } from '../../../models/common/customer';
const showStopAllAuthorizationLink = (paymentCards) => {
    if (!(paymentCards === null || paymentCards === void 0 ? void 0 : paymentCards.length))
        return null;
    return (paymentCards.find((card) => { var _a; return ((_a = card === null || card === void 0 ? void 0 : card.operations) === null || _a === void 0 ? void 0 : _a.showAuthStatus) === 'Authorized'; }) || null);
};
export const CreditCardList = (props) => {
    const { customerRole, customerType } = props;
    const [deletedCreditCardIndex, setDeletedCreditCardIndex] = useState(null);
    const { loading, creditCards, alertMessage, paginationInfo, currentPageNumber, currentSortByValue, getPaymentCards, setAlertMessage, submittedSearchType, isSearchActive, submittedSearchValue, isListView, setIsListView, proAllowanceMessage, getAccountProfile, } = usePayments();
    const isMountedRef = useIsMounted();
    const { channel } = useContext(ExperienceContext);
    const [isBulkAddAvailable, setIsBulkAddAvailable] = useState(false);
    const isB2B = customerType === 'B2B';
    const isMobile = channel === 'mobile';
    const screen = useBreakpoint('lg');
    const isSmallScreen = screen.lesser;
    const showStopAuthBtn = !!showStopAllAuthorizationLink(creditCards === null || creditCards === void 0 ? void 0 : creditCards.paymentCards);
    const isAdmin = customerRole === CustomerRole.ADMIN;
    const shouldCheckForBulkAdd = !isMobile && isB2B && isAdmin;
    const paymentCards = !isB2B
        ? creditCards === null || creditCards === void 0 ? void 0 : creditCards.paymentCards.slice(PAYMENTS_PAGE_SIZE * (currentPageNumber - 1), PAYMENTS_PAGE_SIZE * currentPageNumber)
        : creditCards === null || creditCards === void 0 ? void 0 : creditCards.paymentCards;
    const { hasHDWallet, hasProTradeCredit } = useLoyaltyMembershipDetails();
    const getTotalPages = () => {
        if (isB2B) {
            return Number(paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalPages) || 0;
        }
        return (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCreditCards)
            ? Math.ceil((paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCreditCards) / PAYMENTS_PAGE_SIZE)
            : 0;
    };
    const totalPages = getTotalPages();
    useEffect(() => {
        return () => {
            setAlertMessage(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (shouldCheckForBulkAdd && isMountedRef.current) {
            getAccountProfile().then(({ customerProfile }) => {
                if (isMountedRef.current) {
                    setIsBulkAddAvailable(customerProfile.bulkAdd || false);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMountedRef, shouldCheckForBulkAdd]);
    const handleSuccess = (paymentId, message, dataTestId) => {
        setAlertMessage({
            paymentId,
            alertStatus: TAlertStatus.SUCCESS,
            text: message,
            dataTestId,
        });
        if (message === DELETED_CARD_MSG.message) {
            const newPage = getNewPageNumber(currentPageNumber, totalPages, isB2B ? paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.paymentsOnCurrentPage : paymentCards === null || paymentCards === void 0 ? void 0 : paymentCards.length);
            // ensure user stays on the same page or on the previous page (if last payment deleted) to show delete confirmation
            onChangePage(newPage);
            return;
        }
        onChangePage(1);
    };
    const onChangePage = (newPageNumber) => {
        triggerGetPaymentCards(newPageNumber, submittedSearchType, submittedSearchValue, currentSortByValue.sortBy, !!currentSortByValue.ascendingOrder, currentSortByValue.pageSize);
    };
    const handleError = (paymentId, message, dataTestId) => {
        setAlertMessage({
            paymentId,
            alertStatus: TAlertStatus.ERROR,
            text: message,
            dataTestId,
        });
    };
    const triggerGetPaymentCards = (pageNumber, fType, fValue, sortBy, ascendingOrder, pageSize) => {
        return getPaymentCards(CardType.CREDIT_CARD, false, hasProTradeCredit, {
            page: pageNumber,
            sortBy: sortBy ? cardSortByMap[sortBy].value : undefined,
            ascendingOrder: ascendingOrder !== null && ascendingOrder !== void 0 ? ascendingOrder : undefined,
            pageSize,
        }, {
            filterType: fType,
            filterValue: fValue,
        });
    };
    const resetResults = (tableView) => {
        const pageSize = tableView ? DEFAULT_ROWS_PER_PAGE : PAYMENTS_PAGE_SIZE;
        const fType = B2BCreditCardsFilterType.NOTHING;
        const fValue = '';
        triggerGetPaymentCards(1, fType, fValue, null, null, pageSize);
    };
    const handleToggleView = () => {
        setAlertMessage(null);
        setIsListView((prevState) => {
            resetResults(!prevState);
            return !prevState;
        });
        setOtherButtonSubmissionAnalytics(`view toggle: ${!isListView ? 'list' : 'grid'} view`);
    };
    const successMessage = alertMessage &&
        alertMessage.alertStatus === TAlertStatus.SUCCESS && (React.createElement("div", { className: isMobile ? 'sui-mt-4' : 'lg:sui-mb-8' },
        React.createElement(Alert, { "data-testid": alertMessage.dataTestId, status: alertMessage.alertStatus, onClose: () => setAlertMessage(null) }, alertMessage.text)));
    const desktopErrorMessage = !isMobile &&
        alertMessage &&
        alertMessage.alertStatus === TAlertStatus.ERROR && (React.createElement("div", { className: "lg:sui-mb-8" },
        React.createElement(Alert, { "data-testid": alertMessage.dataTestId, status: alertMessage.alertStatus, onClose: () => setAlertMessage(null) }, alertMessage.text)));
    const HDCCMessage = (React.createElement(Alert, { status: "info", "data-testid": "infoMsg" },
        React.createElement("span", { className: "sui-text-base sui-flex sui-flex-wrap" },
            "Visit the\u00A0",
            React.createElement(Link, { href: CREDIT_CARDS_PAGE_URL }, "Home Depot Credit Cards page"),
            "\u00A0to pay and manage your card.")));
    const getCreditCardsList = () => {
        const creditCardList = (paymentCards === null || paymentCards === void 0 ? void 0 : paymentCards.map((card, index) => (React.createElement("div", { key: card.paymentId, className: `sui-flex ${isMobile ? 'sui-flex-col' : ''}` },
            React.createElement(CreditCard, { isMobile: isMobile, key: card.paymentId, card: card, setDeletedCreditCardIndex: setDeletedCreditCardIndex, index: index, onActionSuccess: handleSuccess, onActionFailure: handleError, canShowHDWallet: hasHDWallet }))))) || null;
        if (isMobile &&
            deletedCreditCardIndex !== null &&
            creditCardList !== null) {
            creditCardList.splice(deletedCreditCardIndex, 0, React.createElement(React.Fragment, null, successMessage));
        }
        return creditCardList;
    };
    const mapReadableSearchValue = () => {
        let value = submittedSearchValue;
        if (submittedSearchType === B2BCreditCardsFilterType.cardBrand &&
            submittedSearchValue in CreditCardTypesTextMapping) {
            value =
                CreditCardTypesTextMapping[submittedSearchValue];
        }
        return value;
    };
    const readableSearchValue = mapReadableSearchValue();
    return (React.createElement(React.Fragment, null,
        React.createElement(Spinner, { active: loading }),
        React.createElement(PaymentsHeader, { cardType: CardType.CREDIT_CARD, onActionSuccess: handleSuccess, onActionFailure: handleError, isBulkAddAvailable: isBulkAddAvailable }),
        !(proAllowanceMessage === null || proAllowanceMessage === void 0 ? void 0 : proAllowanceMessage.hideHDCC) && HDCCMessage,
        !isMobile && successMessage,
        proAllowanceMessage && proAllowanceMessage.content,
        desktopErrorMessage,
        React.createElement(PaymentsHeaderSecondaryButtons, { cardType: CardType.CREDIT_CARD, showStopAuthBtn: showStopAuthBtn, onActionSuccess: handleSuccess, onActionFailure: handleError }),
        !isSearchActive && hasNoPaymentCards(creditCards) && (React.createElement(NoPaymentCardsMessage, { tenderType: CardType.CREDIT_CARD })),
        (!hasNoPaymentCards(creditCards) || isSearchActive) && isB2B && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: isListView
                    ? 'sui-flex sui-items-center sui-gap-10 2xl:sui-gap-0'
                    : 'sui-mb-4' },
                React.createElement(CreditCardSortSearchBar, { triggerGetPaymentCards: triggerGetPaymentCards, onResetResults: resetResults }),
                !isSearchActive && !isSmallScreen && (React.createElement(CardViewToggle, { isListView: isListView, onChangeToggle: handleToggleView }))),
            isSearchActive && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "sui-pb-5" },
                    React.createElement(Typography, { variant: "body-lg", weight: "bold" },
                        (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalSearchedCreditCards) === 0
                            ? 'No'
                            : paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalSearchedCreditCards,
                        ' ',
                        "Result",
                        (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalSearchedCreditCards) !== 1 && 's',
                        " Found")),
                !!readableSearchValue &&
                    (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalSearchedCreditCards) === 0 && (React.createElement(Typography, { variant: "body-base" },
                    "We couldn't find results for \"",
                    readableSearchValue,
                    "\". Review your search or try a new search.")))))),
        isListView && !isSmallScreen && isB2B ? (React.createElement(TableView, { triggerGetPaymentCards: triggerGetPaymentCards, onChangePage: onChangePage, onActionSuccess: handleSuccess, onActionFailure: handleError, canShowHDWallet: hasHDWallet })) : (React.createElement(React.Fragment, null,
            React.createElement("div", { "data-testid": "GridViewWrapper", className: "sui-grid sui-grid-cols-1 sui-gap-y-4 sui-gap-x-8 md:sui-grid-cols-2 2xl:sui-gap-y-16 2xl:sui-grid-cols-3" }, getCreditCardsList()),
            totalPages > 1 && (React.createElement("div", { className: "sui-w-full sui-justify-center sui-p-5 sui-flex" },
                React.createElement(Pagination, { nPages: totalPages, onChangePage: (newPage) => {
                        onChangePage(newPage);
                        setAlertMessage(null);
                        scrollToTop();
                    }, page: currentPageNumber })))))));
};
